import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useFleetData, loadAllBlueprints, SHIPS_LIST_COLS } from './FleetDataContext';
import { supabase } from './supabaseClient';
import Nav from './Nav';
import { EventCard, normalizeAttendees as normalizeEventAttendees, calculateEventPayouts, getCompensationCurrency, isEventCompleted } from './EventsPage';
import FleetFilterBar, { applyFleetFilter, getShipStatus, SHIP_STATUS } from './FleetFilterBar';
import { FleetExportModal, FleetImportModal } from './FleetExportImportModals';
import { BLUEPRINT_SECTIONS, blueprintSectionOf } from './blueprintSections';

const ACCENT = '#ce93d8';
const DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// (BLUEPRINT_SECTIONS + blueprintSectionOf live in ./blueprintSections so
// the org-wide BlueprintsPage tracker can use the exact same buckets.)

const NAV_LINKS = [
  { label: 'Home', href: '/', external: false },
  { label: 'Hierarchy', href: '/hierarchy', external: false },
  { label: 'Fleet', href: '/fleet', external: false },
  { label: 'Events', href: '/events', external: false },
];

function StarField() {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      a: Math.random(), speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed;
        if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.1;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh', pointerEvents: 'none', zIndex: 0 }} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, height: 2, background: ACCENT, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: 'uppercase' }}>{children}</span>
    </div>
  );
}

function fmtHour(h) {
  h = Math.round(((h % 24) + 24) % 24);
  if (h === 0 || h === 24) return '12 am';
  if (h === 12) return '12 pm';
  return h < 12 ? `${h} am` : `${h - 12} pm`;
}

// ─── Profile Analytics (Super Admin Only) ─────────────────────────────────────
function ProfileAnalytics({ profileId, profileName }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      const [{ data: attendance }, { data: ratings }, { data: sessions }, { data: allEvents }] = await Promise.all([
        supabase.from("event_attendance").select("*, event_reviews(overall_rating)").eq("user_id", profileId),
        supabase.from("event_ratings").select("*").eq("user_id", profileId),
        supabase.from("user_sessions").select("started_at, last_active_at").eq("user_id", profileId).order("started_at", { ascending: false }).limit(100),
        supabase.from("events").select("id, title, date, type, attendees, archived").eq("archived", false),
      ]);

      // Compute attendance score: events attended / events signed up for
      const signedUpEvents = (allEvents || []).filter(ev => {
        const att = Array.isArray(ev.attendees) ? ev.attendees : [];
        return att.some(a => (typeof a === 'string' ? a : a.user_id) === profileId);
      });
      const attendedCount = (attendance || []).filter(a => a.showed_up).length;
      const signedUpCount = signedUpEvents.length;
      const attendanceScore = signedUpCount > 0 ? ((attendedCount / signedUpCount) * 100).toFixed(0) : 0;

      // Average star rating from event reviews
      const userRatings = (ratings || []).map(r => r.rating);
      const avgRating = userRatings.length > 0 ? (userRatings.reduce((s, r) => s + r, 0) / userRatings.length).toFixed(1) : 0;

      // Feedback log: attendance records with notes
      const feedbackLog = (attendance || []).filter(a => a.notes && a.notes.trim().length > 0).map(a => {
        const ev = (allEvents || []).find(e => e.id === a.event_id);
        return {
          eventTitle: ev?.title || "Unknown Event",
          eventDate: ev?.date || "",
          eventType: ev?.type || "Other",
          showed_up: a.showed_up,
          brought_value: a.brought_value,
          notes: a.notes,
          created_at: a.created_at,
        };
      }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Value score: events where brought_value was true
      const valuableCount = (attendance || []).filter(a => a.brought_value).length;

      setData({
        attendanceScore: parseFloat(attendanceScore),
        attendedCount,
        signedUpCount,
        avgRating: parseFloat(avgRating),
        ratingCount: userRatings.length,
        feedbackLog,
        sessionCount: (sessions || []).length,
        valuableCount,
      });
      setLoading(false);
    }
    load();
  }, [profileId]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>
        LOADING ANALYTICS...
      </div>
    );
  }

  if (!data) return null;

  const ACCENT_GOLD = '#fcd657';
  const TYPE_COLORS = {
    Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa",
    Training: "#4fc3f7", Piracy: "#f87171", Exploration: "#34d399", Other: "#ce93d8",
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 32, height: 2, background: ACCENT_GOLD }} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT_GOLD, textTransform: 'uppercase' }}>Super Admin Analytics</span>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        {/* Star Rating */}
        <div style={{ padding: '20px 24px', border: '1px solid rgba(252,214,87,0.25)', background: 'rgba(252,214,87,0.04)', flex: '1 1 140px', minWidth: 140 }}>
          <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
            {[1, 2, 3, 4].map(s => (
              <span key={s} style={{ fontSize: 20, color: s <= Math.round(data.avgRating) ? ACCENT_GOLD : 'rgba(255,255,255,0.1)' }}>★</span>
            ))}
          </div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: ACCENT_GOLD, lineHeight: 1, marginBottom: 4 }}>
            {data.avgRating > 0 ? data.avgRating : '—'}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.35)', textTransform: 'uppercase' }}>
            Avg Rating ({data.ratingCount} events)
          </div>
        </div>

        {/* Attendance Score */}
        <div style={{ padding: '20px 24px', border: '1px solid rgba(52,211,153,0.25)', background: 'rgba(52,211,153,0.04)', flex: '1 1 140px', minWidth: 140 }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 36, color: data.attendanceScore >= 80 ? '#34d399' : data.attendanceScore >= 50 ? '#fcd657' : '#ef5350', lineHeight: 1, marginBottom: 6 }}>
            {data.signedUpCount > 0 ? `${data.attendanceScore}%` : '—'}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.35)', textTransform: 'uppercase' }}>
            Attendance Score
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.25)', marginTop: 4 }}>
            {data.attendedCount}/{data.signedUpCount} events attended
          </div>
        </div>

        {/* Value Score */}
        <div style={{ padding: '20px 24px', border: '1px solid rgba(79,195,247,0.25)', background: 'rgba(79,195,247,0.04)', flex: '1 1 140px', minWidth: 140 }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 36, color: '#4fc3f7', lineHeight: 1, marginBottom: 6 }}>
            {data.valuableCount}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.35)', textTransform: 'uppercase' }}>
            Events w/ Value
          </div>
        </div>

        {/* Site Visits */}
        <div style={{ padding: '20px 24px', border: '1px solid rgba(167,139,250,0.25)', background: 'rgba(167,139,250,0.04)', flex: '1 1 140px', minWidth: 140 }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 36, color: '#a78bfa', lineHeight: 1, marginBottom: 6 }}>
            {data.sessionCount}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.35)', textTransform: 'uppercase' }}>
            Site Sessions
          </div>
        </div>
      </div>

      {/* Feedback Log */}
      <div style={{ border: '1px solid rgba(252,214,87,0.15)', background: 'rgba(252,214,87,0.02)', padding: 'clamp(16px, 3vw, 24px)' }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT_GOLD, textTransform: 'uppercase', marginBottom: 16 }}>
          Event Feedback Log ({data.feedbackLog.length})
        </div>

        {data.feedbackLog.length === 0 ? (
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 2, textAlign: 'center', padding: 30 }}>
            NO FEEDBACK RECORDED YET
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.feedbackLog.map((f, i) => {
              const evColor = TYPE_COLORS[f.eventType] || '#ce93d8';
              return (
                <div key={i} style={{ padding: '12px 16px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: '#e8eaf6' }}>{f.eventTitle}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: evColor, textTransform: 'uppercase' }}>{f.eventType}</span>
                    {f.eventDate && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.25)' }}>{new Date(f.eventDate).toLocaleDateString()}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: '2px 6px', border: `1px solid ${f.showed_up ? '#34d39955' : '#ef535055'}`, color: f.showed_up ? '#34d399' : '#ef5350', background: f.showed_up ? 'rgba(52,211,153,0.08)' : 'rgba(239,83,80,0.08)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {f.showed_up ? 'ATTENDED' : 'ABSENT'}
                    </span>
                    {f.brought_value && (
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: '2px 6px', border: '1px solid #4fc3f755', color: '#4fc3f7', background: 'rgba(79,195,247,0.08)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        VALUABLE
                      </span>
                    )}
                  </div>
                  <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: 'rgba(232,234,246,0.5)', lineHeight: 1.5, margin: 0 }}>{f.notes}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { profileId } = useParams();
  const { user, profile: myProfile, signOut, isAdmin, loading: authLoading, updateProfile: updateAuthProfile } = useAuth();
  const { ships: preloadedShips, events: preloadedEvents, blueprints: preloadedBlueprints, loaded: preloaded, refreshEvents } = useFleetData();

  const [viewedProfile, setViewedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [availableShips, setAvailableShips] = useState([]);
  const [shipSearch, setShipSearch] = useState('');
  const [showShipPicker, setShowShipPicker] = useState(false);
  const [availableBlueprints, setAvailableBlueprints] = useState([]);
  const [blueprintSearch, setBlueprintSearch] = useState('');
  const [blueprintCategoryFilter, setBlueprintCategoryFilter] = useState('');
  const [showBlueprintPicker, setShowBlueprintPicker] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [userReminders, setUserReminders] = useState({});
  const [profileToast, setProfileToast] = useState(null);

  const [form, setForm] = useState({
    bio: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    home_base: '',
    primary_team: '',
    ships: [],
    blueprints: [],
    availability: {},
  });
  const [homeBaseOptions, setHomeBaseOptions] = useState([]);
  const [homeBaseSystem, setHomeBaseSystem] = useState('');

  // Market data
  const [myListings, setMyListings] = useState([]);
  const [myTransactions, setMyTransactions] = useState([]);
  const [mySellHistory, setMySellHistory] = useState([]);
  const [meritBalance, setMeritBalance] = useState(null);

  // Directory popup — opened by clicking Home Base / Timezone overview cards.
  // Shows a list of members who share the same value so the user can find friends/squadmates.
  const [directoryModal, setDirectoryModal] = useState(null); // { type: 'home_base' | 'timezone', value, members, loading }

  // UTC offset (in fractional hours) for a given IANA timezone, evaluated now.
  // Used for the directory modal's "nearby timezones" matching so EU members
  // 1h apart still find each other instead of strict equality.
  // Returns a compact UTC-offset label for any IANA timezone (e.g. "UTC-5",
  // "UTC+9:30") — used to annotate dropdown options so members can pick the
  // right tz at a glance without having to know city↔offset mapping.
  function formatUtcOffset(timezone) {
    const hours = getUtcOffsetHours(timezone);
    if (!Number.isFinite(hours)) return "";
    const sign = hours < 0 ? "-" : "+";
    const abs = Math.abs(hours);
    const whole = Math.floor(abs);
    const minutes = Math.round((abs - whole) * 60);
    return minutes === 0 ? `UTC${sign}${whole}` : `UTC${sign}${whole}:${String(minutes).padStart(2, "0")}`;
  }

  function getUtcOffsetHours(timezone) {
    if (!timezone) return 0;
    try {
      const now = new Date();
      const localStr = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).format(now);
      const [datePart, timePart] = localStr.split(', ');
      const [m, d, y] = datePart.split('/').map(Number);
      const [hh, mm, ss] = timePart.split(':').map(Number);
      const tzAsUtc = Date.UTC(y, m - 1, d, hh, mm, ss);
      return (tzAsUtc - now.getTime()) / 3600000;
    } catch { return 0; }
  }

  const openDirectoryModal = async (type, value) => {
    if (!value || value === '—') return;
    setDirectoryModal({ type, value, members: [], loading: true });
    if (type === 'timezone') {
      // Pull everyone with a timezone set, then rank by UTC-offset distance
      // from the clicked tz. Keep ±6 hours so the list doesn't get noisy.
      const { data } = await supabase
        .from('profiles')
        .select('id, discord_username, discord_avatar, org_role, home_base, timezone, availability')
        .not('timezone', 'is', null);
      const targetOffset = getUtcOffsetHours(value);
      const enriched = (data || []).map(m => {
        const off = getUtcOffsetHours(m.timezone);
        return { ...m, offsetHours: off, distanceHours: Math.abs(off - targetOffset) };
      });
      const filtered = enriched
        .filter(m => m.distanceHours <= 6)
        .sort((a, b) => a.distanceHours - b.distanceHours || (a.discord_username || '').localeCompare(b.discord_username || ''));
      setDirectoryModal({ type, value, members: filtered, loading: false, targetOffset });
      return;
    }
    // home_base: strict equality is still useful (locations don't have neighbours)
    const { data } = await supabase
      .from('profiles')
      .select('id, discord_username, discord_avatar, org_role, home_base, timezone')
      .eq(type, value)
      .order('discord_username');
    setDirectoryModal({ type, value, members: data || [], loading: false });
  };

  // ── Load profile ────────────────────────────────────────────────────────────
  useEffect(() => {
    // Viewing someone else by ID — fetch immediately, no auth needed
    if (!profileId) return;
    let cancelled = false;
    async function load() {
      setProfileLoading(true);
      const { data } = await supabase.from('profiles').select('*').eq('id', profileId).single();
      if (!cancelled) {
        setViewedProfile(data || null);
        setProfileLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [profileId]);

  useEffect(() => {
    // Own profile — wait for auth to finish
    if (profileId) return;
    if (authLoading) return;
    setViewedProfile(myProfile);
    setProfileLoading(false);
  }, [profileId, authLoading, myProfile]);

  // Use preloaded ships from context, fall back to fetching if context isn't ready
  useEffect(() => {
    if (preloadedShips && preloadedShips.length > 0) {
      setAvailableShips(preloadedShips);
    } else {
      supabase.from('ships').select(SHIPS_LIST_COLS).order('name').then(({ data }) => setAvailableShips(data || []));
    }
  }, [preloadedShips]);

  // Same pattern for blueprints — paginated fallback so we don't truncate at
  // the Supabase 1000-row default (catalog is now ~1.8k after sc_items copy).
  useEffect(() => {
    if (preloadedBlueprints && preloadedBlueprints.length > 0) {
      setAvailableBlueprints(preloadedBlueprints);
    } else {
      loadAllBlueprints().then(data => setAvailableBlueprints(data || []));
    }
  }, [preloadedBlueprints]);

  // Load teams the viewed profile belongs to. Archived teams are filtered out
  // here so they don't appear in any of the team-affiliation surfaces — both
  // the inline chips and the Team Affiliations card read from userTeams.
  useEffect(() => {
    if (!viewedProfile) return;
    async function loadUserTeams() {
      const { data: allTeams } = await supabase.from('teams').select('*');
      if (!allTeams) return;
      const profileTeams = Array.isArray(viewedProfile.teams) ? viewedProfile.teams : [];
      const legacyTeam = viewedProfile.team;
      const matched = allTeams.filter(t =>
        !t.archived && (profileTeams.includes(t.name) || (legacyTeam && t.name === legacyTeam))
      );
      setUserTeams(matched);
    }
    loadUserTeams();
  }, [viewedProfile]);

  // Load home base options
  useEffect(() => {
    async function loadHomeBases() {
      const { data } = await supabase.from('home_bases').select('*').order('system').order('location');
      setHomeBaseOptions(data || []);
    }
    loadHomeBases();
  }, []);

  // Timezone groups for dropdown
  const TIMEZONE_GROUPS = [
    { label: 'Americas', timezones: [
      { value: 'America/New_York', label: 'US Eastern (New York)' },
      { value: 'America/Chicago', label: 'US Central (Chicago)' },
      { value: 'America/Denver', label: 'US Mountain (Denver)' },
      { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
      { value: 'America/Anchorage', label: 'US Alaska (Anchorage)' },
      { value: 'Pacific/Honolulu', label: 'US Hawaii (Honolulu)' },
      { value: 'America/Phoenix', label: 'US Arizona (Phoenix)' },
      { value: 'America/Toronto', label: 'Canada Eastern (Toronto)' },
      { value: 'America/Vancouver', label: 'Canada Pacific (Vancouver)' },
      { value: 'America/Edmonton', label: 'Canada Mountain (Edmonton)' },
      { value: 'America/Winnipeg', label: 'Canada Central (Winnipeg)' },
      { value: 'America/Halifax', label: 'Canada Atlantic (Halifax)' },
      { value: 'America/St_Johns', label: 'Canada Newfoundland (St Johns)' },
      { value: 'America/Mexico_City', label: 'Mexico (Mexico City)' },
      { value: 'America/Sao_Paulo', label: 'Brazil (Sao Paulo)' },
      { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
      { value: 'America/Bogota', label: 'Colombia (Bogota)' },
      { value: 'America/Lima', label: 'Peru (Lima)' },
      { value: 'America/Santiago', label: 'Chile (Santiago)' },
    ]},
    { label: 'Europe', timezones: [
      { value: 'Europe/London', label: 'UK (London)' },
      { value: 'Europe/Dublin', label: 'Ireland (Dublin)' },
      { value: 'Europe/Paris', label: 'France (Paris)' },
      { value: 'Europe/Berlin', label: 'Germany (Berlin)' },
      { value: 'Europe/Amsterdam', label: 'Netherlands (Amsterdam)' },
      { value: 'Europe/Brussels', label: 'Belgium (Brussels)' },
      { value: 'Europe/Madrid', label: 'Spain (Madrid)' },
      { value: 'Europe/Rome', label: 'Italy (Rome)' },
      { value: 'Europe/Zurich', label: 'Switzerland (Zurich)' },
      { value: 'Europe/Vienna', label: 'Austria (Vienna)' },
      { value: 'Europe/Stockholm', label: 'Sweden (Stockholm)' },
      { value: 'Europe/Oslo', label: 'Norway (Oslo)' },
      { value: 'Europe/Copenhagen', label: 'Denmark (Copenhagen)' },
      { value: 'Europe/Helsinki', label: 'Finland (Helsinki)' },
      { value: 'Europe/Warsaw', label: 'Poland (Warsaw)' },
      { value: 'Europe/Prague', label: 'Czech Republic (Prague)' },
      { value: 'Europe/Bucharest', label: 'Romania (Bucharest)' },
      { value: 'Europe/Athens', label: 'Greece (Athens)' },
      { value: 'Europe/Moscow', label: 'Russia (Moscow)' },
      { value: 'Europe/Istanbul', label: 'Turkey (Istanbul)' },
    ]},
    { label: 'Asia / Pacific', timezones: [
      { value: 'Asia/Dubai', label: 'UAE (Dubai)' },
      { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
      { value: 'Asia/Bangkok', label: 'Thailand (Bangkok)' },
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Shanghai', label: 'China (Shanghai)' },
      { value: 'Asia/Tokyo', label: 'Japan (Tokyo)' },
      { value: 'Asia/Seoul', label: 'South Korea (Seoul)' },
      { value: 'Asia/Taipei', label: 'Taiwan (Taipei)' },
      { value: 'Asia/Manila', label: 'Philippines (Manila)' },
      { value: 'Asia/Jakarta', label: 'Indonesia (Jakarta)' },
    ]},
    { label: 'Oceania', timezones: [
      { value: 'Australia/Sydney', label: 'Australia Eastern (Sydney)' },
      { value: 'Australia/Melbourne', label: 'Australia (Melbourne)' },
      { value: 'Australia/Brisbane', label: 'Australia (Brisbane)' },
      { value: 'Australia/Perth', label: 'Australia Western (Perth)' },
      { value: 'Australia/Adelaide', label: 'Australia Central (Adelaide)' },
      { value: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
    ]},
    { label: 'Africa / Middle East', timezones: [
      { value: 'Africa/Cairo', label: 'Egypt (Cairo)' },
      { value: 'Africa/Lagos', label: 'Nigeria (Lagos)' },
      { value: 'Africa/Johannesburg', label: 'South Africa (Johannesburg)' },
      { value: 'Africa/Nairobi', label: 'Kenya (Nairobi)' },
      { value: 'Asia/Jerusalem', label: 'Israel (Jerusalem)' },
      { value: 'Asia/Riyadh', label: 'Saudi Arabia (Riyadh)' },
    ]},
  ];

  // Load events the viewed profile is attending — use preloaded data if available
  useEffect(() => {
    if (!viewedProfile) return;
    function filterAttending(allEvents) {
      return allEvents.filter(ev => {
        if (ev.archived) return false;
        const attendees = Array.isArray(ev.attendees) ? ev.attendees : [];
        return attendees.some(a =>
          (typeof a === 'string' && a === viewedProfile.id) ||
          (typeof a === 'object' && a !== null && a.user_id === viewedProfile.id)
        );
      });
    }
    if (preloaded && preloadedEvents.length >= 0) {
      setUserEvents(filterAttending(preloadedEvents));
    } else {
      async function loadUserEvents() {
        const { data: allEvents } = await supabase.from('events').select('*').eq('archived', false).order('date');
        if (!allEvents) return;
        setUserEvents(filterAttending(allEvents));
      }
      loadUserEvents();
    }
  }, [viewedProfile, preloaded, preloadedEvents]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!profileToast) return;
    const t = setTimeout(() => setProfileToast(null), 4000);
    return () => clearTimeout(t);
  }, [profileToast]);

  function showProfileToast(message, type = "success") {
    setProfileToast({ message, type });
  }

  // Load reminder subscriptions for the logged-in user
  useEffect(() => {
    if (!myProfile) return;
    async function loadReminders() {
      try {
        const { data } = await supabase
          .from("event_reminders")
          .select("event_id, reminder_type, remind_at")
          .eq("user_id", myProfile.id)
          .eq("sent", false);
        const map = {};
        (data || []).forEach(r => {
          if (!map[r.event_id]) map[r.event_id] = [];
          map[r.event_id].push({ type: r.reminder_type, remind_at: r.remind_at });
        });
        setUserReminders(map);
      } catch (e) { /* table may not exist yet */ }
    }
    loadReminders();
  }, [myProfile, userEvents]);

  // Load market data + merit balance for this profile
  useEffect(() => {
    if (!viewedProfile) return;
    async function loadMarketData() {
      try {
        const [{ data: listings }, { data: buyTx }, { data: sellTx }, { data: balanceRows }] = await Promise.all([
          supabase.from('store_listings').select('*').eq('listed_by', viewedProfile.id).neq('status', 'removed').order('created_at', { ascending: false }),
          supabase.from('store_transactions').select('*, listing:store_listings(title, image_url)').eq('buyer_id', viewedProfile.id).order('created_at', { ascending: false }),
          supabase.from('store_transactions').select('*, listing:store_listings(title, image_url), buyer:profiles!store_transactions_buyer_id_fkey(discord_username)').eq('seller_id', viewedProfile.id).eq('status', 'confirmed').order('completed_at', { ascending: false }),
          supabase.from('merit_balances').select('*').eq('user_id', viewedProfile.id),
        ]);
        setMyListings(listings || []);
        setMyTransactions(buyTx || []);
        setMySellHistory(sellTx || []);
        setMeritBalance(balanceRows && balanceRows.length > 0 ? balanceRows[0] : { total_earned: 0, pending: 0, total_spent: 0, pending_spent: 0, available: 0 });
      } catch (err) {
        console.warn('Store tables not available yet:', err.message);
        setMeritBalance({ total_earned: 0, pending: 0, total_spent: 0, pending_spent: 0, available: 0 });
      }
    }
    loadMarketData();
  }, [viewedProfile]);

  // Contractor confirms (or un-confirms) receipt of payment for a contract event.
  // Only touches the row with this user's user_id in the events.payouts array.
  async function confirmContractPayment(event, confirmed) {
    if (!myProfile) return;
    const existing = Array.isArray(event.payouts) ? event.payouts : [];
    // If the leader hasn't saved payouts yet, seed the row from the live calc so
    // we have something to attach confirmation to.
    let rows = [...existing];
    const myIdx = rows.findIndex(p => p.user_id === myProfile.id);
    if (myIdx < 0) {
      const calc = calculateEventPayouts(event);
      const mine = calc.rows.find(r => r.user_id === myProfile.id);
      if (!mine) return;
      rows.push({
        user_id: myProfile.id,
        username: mine.username,
        calculated: mine.total_owed,
        adjustment: 0,
        total_owed: mine.total_owed,
        paid: false, paid_by: null, paid_at: null,
        confirmed: !!confirmed,
        confirmed_at: confirmed ? new Date().toISOString() : null,
        notes: "",
      });
    } else {
      rows[myIdx] = {
        ...rows[myIdx],
        confirmed: !!confirmed,
        confirmed_at: confirmed ? new Date().toISOString() : null,
      };
    }
    await supabase.from('events').update({ payouts: rows }).eq('id', event.id);
    setUserEvents(prev => prev.map(e => e.id === event.id ? { ...e, payouts: rows } : e));
    showProfileToast(confirmed ? '✓ Payment receipt confirmed' : 'Confirmation cleared', 'success');
  }

  async function markListingSold(listingId) {
    await supabase.from('store_listings').update({ status: 'sold_out', updated_at: new Date().toISOString() }).eq('id', listingId);
    setMyListings(prev => prev.map(l => l.id === listingId ? { ...l, status: 'sold_out' } : l));
  }

  async function removeMyListing(listingId) {
    if (!window.confirm('Remove this listing?')) return;
    await supabase.from('store_listings').update({ status: 'removed' }).eq('id', listingId);
    setMyListings(prev => prev.filter(l => l.id !== listingId));
  }

  // RSVP toggle for profile events
  async function profileToggleRSVP(event, selectedOption) {
    if (!myProfile) return;
    const attendees = normalizeEventAttendees(event.attendees);
    const existing = attendees.find(a => a.user_id === myProfile.id);
    let updated;

    const isLeaving = (selectedOption === null && existing) || (selectedOption && !selectedOption.attending);
    const isJoining = selectedOption && selectedOption.attending;

    if (isLeaving) {
      updated = attendees.filter(a => a.user_id !== myProfile.id);
      try {
        await fetch("/api/event-reminders", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: event.id, user_id: myProfile.id }) });
        setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
      } catch (e) { /* non-critical */ }
      // Send decline confirmation DM when explicitly marking a non-attending
      // option (parity with the Discord button flow, which DMs on declines too).
      // If they previously held an attending RSVP, the DM alerts them that
      // they have been removed from the event.
      if (selectedOption && !selectedOption.attending && myProfile.discord_id) {
        const prevOpt = existing?.rsvp_group
          ? (event.rsvp_options || []).find(o => o.name === existing.rsvp_group)
          : null;
        const wasAttending = existing ? (prevOpt ? prevOpt.attending !== false : true) : false;
        try {
          await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_dm", discord_id: myProfile.discord_id, event,
              dm_type: "decline_confirm", rsvp_option_name: selectedOption.name,
              was_attending: wasAttending,
            }),
          });
        } catch (e) { /* non-critical */ }
      }
    } else if (isJoining) {
      if (existing) {
        updated = attendees.map(a => a.user_id === myProfile.id ? { ...a, rsvp_group: selectedOption.name } : a);
      } else {
        updated = [...attendees, {
          user_id: myProfile.id,
          username: myProfile.discord_username || "",
          avatar: myProfile.discord_avatar || "",
          role: "", group: "",
          rsvp_group: selectedOption.name,
          sort_order: null,
        }];
        if (selectedOption.reminders && myProfile.discord_id && event.date) {
          try {
            const remRes = await fetch("/api/event-reminders", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: event.id, user_id: myProfile.id, discord_id: myProfile.discord_id, event_date: event.date, event_time: event.time || "00:00" }) });
            const remData = await remRes.json().catch(() => null);
            const schedule = (remData?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
            setUserReminders(prev => ({ ...prev, [event.id]: schedule.length > 0 ? schedule : [{ type: "pending" }] }));
          } catch (e) { /* non-critical */ }
        }
        // Send signup confirmation DM with reminder buttons
        if (myProfile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: myProfile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    } else {
      // Simple toggle fallback
      if (existing) {
        updated = attendees.filter(a => a.user_id !== myProfile.id);
        try {
          await fetch("/api/event-reminders", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: event.id, user_id: myProfile.id }) });
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
        } catch (e) { /* non-critical */ }
      } else {
        updated = [...attendees, { user_id: myProfile.id, username: myProfile.discord_username || "", avatar: myProfile.discord_avatar || "", role: "", group: "", sort_order: null }];
        // Send signup confirmation DM with reminder buttons
        if (myProfile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: myProfile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    }

    await supabase.from("events").update({ attendees: updated }).eq("id", event.id);
    if (refreshEvents) refreshEvents();
    // Update all Discord embeds with latest RSVP roster
    try {
      fetch("/api/discord-notify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_rsvp_embed", event_id: event.id }),
      });
    } catch (e) { /* non-critical */ }
  }

  // Reminder toggle for profile events — individual types or clear-all
  async function profileToggleReminder(event, reminderType, isRemoving) {
    if (!myProfile || !myProfile.discord_id) return;

    if (isRemoving) {
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id, user_id: myProfile.id,
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        if (reminderType) {
          setUserReminders(prev => {
            const existing = (prev[event.id] || []).filter(r => r.type !== reminderType);
            if (existing.length === 0) { const next = { ...prev }; delete next[event.id]; return next; }
            return { ...prev, [event.id]: existing };
          });
          showProfileToast("Reminder removed", "success");
        } else {
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
          showProfileToast("All reminders cleared", "success");
        }
      } catch (e) { /* non-critical */ }
    } else {
      try {
        const res = await fetch("/api/event-reminders", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id, user_id: myProfile.id,
            discord_id: myProfile.discord_id,
            event_date: event.date, event_time: event.time || "00:00",
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        const data = await res.json().catch(() => null);
        const newReminders = (data?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
        setUserReminders(prev => {
          const existing = prev[event.id] || [];
          const merged = [...existing];
          for (const nr of newReminders) { if (!merged.some(m => m.type === nr.type)) merged.push(nr); }
          return { ...prev, [event.id]: merged };
        });
        const labels = { at_start: "on event start", "10min": "10 minutes before", "1hr": "1 hour before", "24hr": "1 day before", "1week": "1 week before" };
        showProfileToast(`🔔 Reminder set — ${labels[reminderType] || "before this event"}`, "success");
      } catch (e) { /* non-critical */ }
    }
  }

  // Sync form when profile loads
  useEffect(() => {
    if (viewedProfile) {
      // Normalize ships: support both old string[] format and new {ship_id, quantity}[] format
      const rawShips = Array.isArray(viewedProfile.ships) ? viewedProfile.ships : [];
      const normalizedShips = rawShips.map(entry => {
        if (typeof entry === 'object' && entry !== null && entry.ship_id) return entry;
        // Legacy string entry — ignore (will be dropped when user saves)
        return null;
      }).filter(Boolean);

      // Normalize availability: convert old single-object format to array-of-windows
      const rawAvail = viewedProfile.availability || {};
      const normalizedAvail = {};
      for (const day of DAYS) {
        const val = rawAvail[day];
        if (!val) continue;
        if (Array.isArray(val)) {
          normalizedAvail[day] = val.filter(w => w && typeof w.start === 'number' && typeof w.end === 'number');
        } else if (typeof val === 'object' && typeof val.start === 'number' && typeof val.end === 'number') {
          normalizedAvail[day] = [{ start: val.start, end: val.end }];
        }
        // Drop invalid entries
        if (normalizedAvail[day] && normalizedAvail[day].length === 0) delete normalizedAvail[day];
      }

      // Normalize blueprints: each entry just stores { blueprint_id } — boolean ownership.
      // Accept legacy entries with quantity by collapsing to a single record per id.
      const rawBps = Array.isArray(viewedProfile.blueprints) ? viewedProfile.blueprints : [];
      const seenBpIds = new Set();
      const normalizedBps = [];
      rawBps.forEach(entry => {
        const id = typeof entry === 'string' ? entry : (entry && entry.blueprint_id);
        if (id && !seenBpIds.has(id)) {
          seenBpIds.add(id);
          normalizedBps.push({ blueprint_id: id });
        }
      });

      const hb = viewedProfile.home_base || '';
      setForm({
        bio: viewedProfile.bio || '',
        timezone: viewedProfile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        home_base: hb,
        primary_team: viewedProfile.primary_team || '',
        ships: normalizedShips,
        blueprints: normalizedBps,
        availability: normalizedAvail,
      });
      // Sync the auto-save baseline so the next ships/blueprints change can be detected as a real edit
      lastSavedRef.current = {
        ships: JSON.stringify(normalizedShips),
        blueprints: JSON.stringify(normalizedBps),
      };
      // Unblock the auto-save effects now that the form reflects the loaded profile.
      // Until this flag is set, the effects bail out so they can't fire a
      // stale "[]" save before profile data arrives.
      formHydratedRef.current = true;
    }
  }, [viewedProfile]);

  // Pre-select the home base system when options load
  useEffect(() => {
    const hb = form.home_base;
    if (hb && homeBaseOptions.length > 0 && !homeBaseSystem) {
      const match = homeBaseOptions.find(h => h.location === hb);
      if (match) setHomeBaseSystem(match.system);
    }
  }, [homeBaseOptions, form.home_base]);

  const profile = viewedProfile;
  const isOwner = !!user && (!profileId || user.id === profileId);
  const canEdit = isOwner || isAdmin;

  // ── Auto-save for ships and blueprints ─────────────────────────────────────
  // Tracks the last persisted JSON snapshot per field so we only save on real edits
  // (and don't trigger spurious saves when the form first loads from the profile).
  const lastSavedRef = useRef({ ships: null, blueprints: null });
  // Status filter + search for the fleet list. View-only — doesn't affect saves.
  const [fleetFilter, setFleetFilter] = useState({ status: 'all', search: '' });
  // Export / Import (hangar.link JSON) modals — own-profile editors only.
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Gates the auto-save effects until the form has been hydrated from viewedProfile
  // at least once. Without this gate, on initial mount form.ships=[] (the useState
  // default) doesn't match the stale `null` baseline, so the effect schedules a
  // 600ms timer that, if it fires BEFORE the profile data finishes loading, will
  // happily save ships=[] over the user's real fleet. See NKA wipe report.
  const formHydratedRef = useRef(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'

  // Debounced auto-save for form.ships
  useEffect(() => {
    if (!canEdit || !profile?.id) return;
    if (!formHydratedRef.current) return; // wait for first hydration
    const current = JSON.stringify(form.ships || []);
    if (current === lastSavedRef.current.ships) return;
    const timer = setTimeout(async () => {
      setAutoSaveStatus('saving');
      const { error } = await supabase.from('profiles').update({ ships: form.ships }).eq('id', profile.id);
      if (error) {
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus(''), 2500);
      } else {
        lastSavedRef.current.ships = current;
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 1500);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.ships, canEdit, profile?.id]);

  // Debounced auto-save for form.blueprints
  useEffect(() => {
    if (!canEdit || !profile?.id) return;
    if (!formHydratedRef.current) return; // wait for first hydration
    const current = JSON.stringify(form.blueprints || []);
    if (current === lastSavedRef.current.blueprints) return;
    const timer = setTimeout(async () => {
      setAutoSaveStatus('saving');
      const { error } = await supabase.from('profiles').update({ blueprints: form.blueprints }).eq('id', profile.id);
      if (error) {
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus(''), 2500);
      } else {
        lastSavedRef.current.blueprints = current;
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 1500);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.blueprints, canEdit, profile?.id]);

  // ── Helper functions ────────────────────────────────────────────────────────
  function addShipToFleet(ship) {
    const existing = form.ships.find(s => s.ship_id === ship.id);
    if (existing) {
      setForm(p => ({ ...p, ships: p.ships.map(s => s.ship_id === ship.id ? { ...s, quantity: (s.quantity || 1) + 1 } : s) }));
    } else {
      setForm(p => ({ ...p, ships: [...p.ships, { ship_id: ship.id, quantity: 1 }] }));
    }
    setShowShipPicker(false);
    setShipSearch('');
  }

  function updateShipQuantity(shipId, quantity) {
    if (quantity < 1) {
      removeShipFromFleet(shipId);
      return;
    }
    setForm(p => ({ ...p, ships: p.ships.map(s => s.ship_id === shipId ? { ...s, quantity } : s) }));
  }

  function removeShipFromFleet(shipId) {
    setForm(p => ({ ...p, ships: p.ships.filter(s => s.ship_id !== shipId) }));
  }

  function toggleShipPrimary(shipId) {
    setForm(p => ({ ...p, ships: p.ships.map(s => s.ship_id === shipId ? { ...s, primary: !s.primary } : s) }));
  };

  // Flip a single entry between 'pledged' (default) and 'in_game'. Stored on
  // the JSONB row so we don't need a schema change. Filter reads via getShipStatus.
  const toggleShipStatus = (shipId) => {
    setForm(p => ({
      ...p,
      ships: p.ships.map(s => {
        if (s.ship_id !== shipId) return s;
        const next = getShipStatus(s) === SHIP_STATUS.IN_GAME ? SHIP_STATUS.PLEDGED : SHIP_STATUS.IN_GAME;
        return { ...s, status: next };
      }),
    }));
  }

  function getShipInfo(shipId) {
    return availableShips.find(s => s.id === shipId);
  }

  // ── Blueprint helpers (boolean ownership: own / don't own) ────────────────
  function addBlueprintToProfile(bp) {
    setForm(p => {
      const list = p.blueprints || [];
      if (list.some(b => b.blueprint_id === bp.id)) return p; // already owned
      return { ...p, blueprints: [...list, { blueprint_id: bp.id }] };
    });
  }

  function removeBlueprintFromProfile(bpId) {
    setForm(p => ({ ...p, blueprints: (p.blueprints || []).filter(b => b.blueprint_id !== bpId) }));
  }

  function getBlueprintInfo(bpId) {
    return availableBlueprints.find(b => b.id === bpId);
  }

  // Read windows from a given availability object (used inside setForm callbacks with latest state)
  function getWindowsFrom(avail, day) {
    const val = avail[day];
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'object' && typeof val.start === 'number') return [val];
    return [];
  }

  // Read windows from the current form state (used for rendering only)
  function getWindows(day) {
    return getWindowsFrom(form.availability, day);
  }

  function toggleAvailDay(day) {
    setForm(p => {
      const current = getWindowsFrom(p.availability, day);
      return {
        ...p,
        availability: {
          ...p.availability,
          [day]: current.length > 0 ? undefined : [{ start: 18, end: 23 }],
        },
      };
    });
  }

  function updateAvailTime(day, windowIdx, field, value) {
    const parsed = parseInt(value);
    if (isNaN(parsed)) return;
    setForm(p => {
      const windows = [...getWindowsFrom(p.availability, day)];
      if (!windows[windowIdx]) return p;
      windows[windowIdx] = { ...windows[windowIdx], [field]: parsed };
      return {
        ...p,
        availability: {
          ...p.availability,
          [day]: windows,
        },
      };
    });
  }

  function addWindow(day) {
    setForm(p => {
      const windows = [...getWindowsFrom(p.availability, day)];
      const lastEnd = windows.length > 0 ? windows[windows.length - 1].end : 18;
      windows.push({ start: Math.min(lastEnd, 22), end: Math.min(lastEnd + 2, 24) });
      return {
        ...p,
        availability: {
          ...p.availability,
          [day]: windows,
        },
      };
    });
  }

  function removeWindow(day, windowIdx) {
    setForm(p => {
      const windows = getWindowsFrom(p.availability, day).filter((_, i) => i !== windowIdx);
      return {
        ...p,
        availability: {
          ...p.availability,
          [day]: windows.length > 0 ? windows : undefined,
        },
      };
    });
  }

  async function handleSave() {
    if (!canEdit || !profile) return;
    setSaving(true);
    // When editing one's own profile, route through AuthContext.updateProfile so
    // the shared myProfile state is updated in sync — otherwise the useEffect
    // below that mirrors myProfile → viewedProfile would clobber freshly-saved
    // values until a hard refresh.
    const isOwnProfile = !profileId && updateAuthProfile && myProfile && profile.id === myProfile.id;
    if (isOwnProfile) {
      const { data } = await updateAuthProfile(form);
      if (data) setViewedProfile(data);
    } else {
      await supabase.from('profiles').update(form).eq('id', profile.id);
      const { data } = await supabase.from('profiles').select('*').eq('id', profile.id).single();
      setViewedProfile(data);
    }
    setSaving(false);
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 2500);
  }

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (profileLoading) return (
    <div style={{ background: '#05070f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: 'rgba(206,147,216,0.6)' }}>
      LOADING...
    </div>
  );

  if (!profile) return (
    <div style={{ background: '#05070f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: 'rgba(255,255,255,0.2)' }}>
      PROFILE NOT FOUND
    </div>
  );

  const viewerIsSuperAdmin = myProfile?.perm_super_admin === true || myProfile?.admin_tier === "super_admin";

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'ships', label: 'Fleet' },
    { id: 'blueprints', label: 'Blueprints' },
    { id: 'events', label: 'Events' },
    ...(isOwner ? [{ id: 'contracts', label: 'My Contracts' }] : []),
    { id: 'availability', label: 'Availability' },
    { id: 'listings', label: 'Listings' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'sell_history', label: 'Sell History' },
    ...(viewerIsSuperAdmin ? [{ id: 'analytics', label: 'Analytics' }] : []),
  ];

  const roleColor = {
    'CEO': '#fcd657', 'President': '#fcd657', 'Executive Officer': '#fcd657',
    'Director of Strategic Development': '#fcd657', 'Operations Liaison': '#fcd657',
    'Division Lead': '#4fc3f7', 'Director of Industry': '#4fc3f7',
    'Manager of Salvage Operations': '#4fc3f7', 'Surface Operations Officer': '#4fc3f7',
    'Commander': '#4fc3f7', 'Lt. Commander': '#ce93d8',
    'Officer': '#81c784', 'Specialist': '#81c784',
    'Crew': '#e8eaf6', 'Member': 'rgba(232,234,246,0.5)',
  }[profile.org_role] || ACCENT;

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: '#05070f', color: '#e8eaf6', minHeight: '100vh', overflowX: 'hidden' }}>
      <StarField />

      {/* Toast notification */}
      {profileToast && (
        <div style={{
          position: "fixed", top: 80, right: 24, zIndex: 9999,
          padding: "14px 24px", borderRadius: 4,
          background: profileToast.type === "error" ? "rgba(244,67,54,0.95)" : "rgba(76,175,80,0.95)",
          color: "#fff", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600,
          letterSpacing: 0.5, boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          animation: "toastSlideIn 0.3s ease-out",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 380, cursor: "pointer",
        }} onClick={() => setProfileToast(null)}>
          <span>{profileToast.message}</span>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #ce93d8; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #ce93d8; transition: width 0.3s ease; }
        .nav-link:hover { color: #ce93d8; }
        .nav-link:hover::after { width: 100%; }
        .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
        input, select, textarea { color-scheme: dark; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${ACCENT} !important; }
        @keyframes toastSlideIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
      <div className="scanline" />

      <Nav accentColor={ACCENT} />

      {/* PROFILE HEADER */}
      <section style={{ position: 'relative', zIndex: 1, paddingTop: 'calc(100px + env(safe-area-inset-top, 0px))', paddingBottom: 48, paddingLeft: 'clamp(16px, 4vw, 40px)', paddingRight: 'clamp(16px, 4vw, 40px)', borderBottom: '1px solid rgba(206,147,216,0.12)', background: 'rgba(18,9,24,0.5)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'flex-start', gap: 32, flexWrap: 'wrap' }}>

          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', border: `3px solid ${roleColor}`, boxShadow: `0 0 24px ${roleColor}44` }}>
              {profile.discord_avatar
                ? <img src={profile.discord_avatar} alt={profile.discord_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                : <div style={{ width: '100%', height: '100%', background: '#0d1120', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 36, color: ACCENT }}>{profile.discord_username?.charAt(0)}</div>
              }
            </div>
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 200 }}>
            {profile.org_role && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: roleColor, textTransform: 'uppercase', marginBottom: 8 }}>{profile.org_role}</div>
            )}
            <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 'clamp(22px, 4vw, 40px)', color: '#e8eaf6', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {profile.discord_username}
              {profile.member_status === 'guest' && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: '#ff9800', border: '1px solid rgba(255,152,0,0.4)', padding: '2px 8px', background: 'rgba(255,152,0,0.08)', fontWeight: 400 }}>GUEST</span>}
            </h1>
            {profile.bio && (
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: 'rgba(232,234,246,0.55)', lineHeight: 1.7, maxWidth: 500, marginTop: 10 }}>{profile.bio}</p>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
            {canEdit && (
              <button onClick={() => {
                // Edit form only renders inside the Overview tab. Always
                // pull the user there so clicking "Edit Profile" from Ships /
                // Merits / etc. actually shows something.
                if (!editing) setActiveTab('overview');
                setEditing(!editing);
              }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', padding: '10px 22px', border: `2px solid ${ACCENT}`, color: ACCENT, background: editing ? `${ACCENT}15` : 'transparent', cursor: 'pointer', transition: 'all 0.2s', clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}
                onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}20`; }}
                onMouseLeave={e => { e.currentTarget.style.background = editing ? `${ACCENT}15` : 'transparent'; }}>
                {editing ? 'Cancel' : 'Edit Profile'}
              </button>
            )}
            {isOwner && (
              <button onClick={signOut} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', padding: '8px 18px', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(232,234,246,0.4)', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#ef5350'; e.currentTarget.style.borderColor = 'rgba(239,83,80,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(232,234,246,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
                Sign Out
              </button>
            )}
          </div>
        </div>
      </section>

      {/* TABS */}
      <div style={{ position: 'relative', zIndex: 1, borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.2)', display: 'flex', paddingLeft: 'clamp(16px, 4vw, 40px)', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', padding: '14px 22px', border: 'none', cursor: 'pointer', background: activeTab === tab.id ? `${ACCENT}12` : 'transparent', color: activeTab === tab.id ? ACCENT : 'rgba(232,234,246,0.38)', borderBottom: activeTab === tab.id ? `2px solid ${ACCENT}` : '2px solid transparent', transition: 'all 0.2s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto', padding: '48px clamp(16px, 4vw, 40px)' }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <SectionLabel>Edit Profile</SectionLabel>
                <div>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Bio</label>
                  <textarea rows={3} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder="Tell the crew about yourself..."
                    style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(206,147,216,0.25)`, color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Timezone</label>
                  <select value={form.timezone} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', background: '#0a0610', border: `1px solid rgba(206,147,216,0.25)`, color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }}>
                    {/* Show current value if not in the list */}
                    {!TIMEZONE_GROUPS.some(g => g.timezones.some(t => t.value === form.timezone)) && form.timezone && (
                      <option value={form.timezone}>{form.timezone.replace(/_/g, ' ')}{formatUtcOffset(form.timezone) ? ` · ${formatUtcOffset(form.timezone)}` : ""}</option>
                    )}
                    {TIMEZONE_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.timezones.map(tz => {
                          const off = formatUtcOffset(tz.value);
                          return (
                            <option key={tz.value} value={tz.value}>
                              {off ? `${tz.label} · ${off}` : tz.label}
                            </option>
                          );
                        })}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Home Base</label>
                  {(() => {
                    const realOptions = homeBaseOptions.filter(h => h.location && h.location !== "__system__");
                    const systems = [...new Set(homeBaseOptions.map(h => h.system))].sort();
                    const locations = homeBaseSystem ? realOptions.filter(h => h.system === homeBaseSystem) : [];
                    return (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <select value={homeBaseSystem} onChange={e => { setHomeBaseSystem(e.target.value); setForm(p => ({ ...p, home_base: '' })); }}
                          style={{ flex: 1, padding: '10px 14px', background: '#0a0610', border: `1px solid rgba(206,147,216,0.25)`, color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }}>
                          <option value="">Select system...</option>
                          {systems.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={form.home_base} onChange={e => setForm(p => ({ ...p, home_base: e.target.value }))}
                          disabled={!homeBaseSystem}
                          style={{ flex: 1, padding: '10px 14px', background: '#0a0610', border: `1px solid rgba(206,147,216,0.25)`, color: homeBaseSystem ? '#e8eaf6' : 'rgba(232,234,246,0.3)', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, opacity: homeBaseSystem ? 1 : 0.5 }}>
                          <option value="">{homeBaseSystem ? 'Select location...' : 'Choose system first'}</option>
                          {locations.map(h => <option key={h.id} value={h.location}>{h.location}</option>)}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {userTeams.length > 0 && (
                  <div>
                    <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Primary Team</label>
                    <select value={form.primary_team} onChange={e => setForm(p => ({ ...p, primary_team: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', background: '#0a0610', border: `1px solid rgba(206,147,216,0.25)`, color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }}>
                      <option value="">-- None --</option>
                      {userTeams.map(t => (
                        <option key={t.id} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.35)', marginTop: 6, letterSpacing: 1 }}>
                      Your primary team will appear in the navigation bar
                    </div>
                  </div>
                )}
                <button onClick={handleSave} disabled={saving} style={{ padding: '12px 32px', background: 'transparent', border: `2px solid ${ACCENT}`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s', alignSelf: 'flex-start', clipPath: 'polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.color = '#05070f'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = ACCENT; }}>
                  {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
                {[
                  { label: 'Org Role', value: profile.org_role || '—' },
                  {
                    label: 'Home Base',
                    value: profile.home_base || '—',
                    onClick: profile.home_base ? () => openDirectoryModal('home_base', profile.home_base) : null,
                    hint: profile.home_base ? 'See members at this base' : null,
                  },
                  {
                    label: 'Timezone',
                    value: profile.timezone ? profile.timezone.replace(/_/g, ' ').replace(/^.*\//, '') : '—',
                    onClick: profile.timezone ? () => openDirectoryModal('timezone', profile.timezone) : null,
                    hint: profile.timezone ? 'See members in this timezone' : null,
                  },
                  { label: 'Merits Available', value: meritBalance ? (meritBalance.available || 0).toLocaleString() : '—', accent: '#fcd657' },
                  { label: 'Merits Pending', value: meritBalance ? (meritBalance.pending || 0).toLocaleString() : '—', accent: '#ffcc02' },
                  { label: 'Merits Earned', value: meritBalance ? (meritBalance.total_earned || 0).toLocaleString() : '—' },
                  {
                    label: 'Ships',
                    value: `${(Array.isArray(profile.ships) ? profile.ships : []).reduce((sum, s) => sum + (typeof s === 'object' && s ? (s.quantity || 1) : 1), 0)} registered`,
                    onClick: () => setActiveTab('ships'),
                    hint: 'View fleet',
                  },
                ].map(item => {
                  const clickable = !!item.onClick;
                  return (
                    <div
                      key={item.label}
                      onClick={item.onClick || undefined}
                      style={{
                        padding: '18px 20px',
                        border: `1px solid ${item.accent ? item.accent + '22' : 'rgba(255,255,255,0.07)'}`,
                        background: item.accent ? item.accent + '08' : 'rgba(255,255,255,0.02)',
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                      onMouseEnter={clickable ? (e => {
                        e.currentTarget.style.borderColor = `${ACCENT}66`;
                        e.currentTarget.style.background = `${ACCENT}10`;
                      }) : undefined}
                      onMouseLeave={clickable ? (e => {
                        e.currentTarget.style.borderColor = item.accent ? item.accent + '22' : 'rgba(255,255,255,0.07)';
                        e.currentTarget.style.background = item.accent ? item.accent + '08' : 'rgba(255,255,255,0.02)';
                      }) : undefined}
                    >
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: item.accent || ACCENT, textTransform: 'uppercase', marginBottom: 8 }}>{item.label}</div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 17, color: '#e8eaf6' }}>{item.value}</div>
                      {clickable && item.hint && (
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: 'rgba(232,234,246,0.32)', marginTop: 6, textTransform: 'uppercase' }}>{item.hint} →</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Merits eligibility disclaimers — surfaced beneath the merits cards so
                viewers understand who qualifies and what happens on departure. */}
            {!editing && (
              <div style={{ marginTop: 14, padding: '12px 16px', border: '1px solid rgba(252,214,87,0.18)', background: 'rgba(252,214,87,0.04)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: '#fcd657', textTransform: 'uppercase', marginBottom: 2 }}>
                  Merit Policy
                </div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: 'rgba(232,234,246,0.7)', lineHeight: 1.55 }}>
                  Only org members are eligible to earn merits.
                </div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: 'rgba(232,234,246,0.7)', lineHeight: 1.55 }}>
                  If you leave the org, all earned and pending merits are immediately forfeited.
                </div>
              </div>
            )}

            {/* Team Affiliations */}
            {userTeams.length > 0 && (
              <div style={{ marginTop: editing ? 0 : 28 }}>
                <SectionLabel>Team Affiliations</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
                  {userTeams.map(team => (
                    <Link key={team.id} to={`/teams/${team.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
                        border: `1px solid ${(team.accent_color || ACCENT)}44`,
                        background: `${(team.accent_color || ACCENT)}08`,
                        transition: 'all 0.2s', textDecoration: 'none', color: 'inherit',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = `${(team.accent_color || ACCENT)}88`; e.currentTarget.style.background = `${(team.accent_color || ACCENT)}15`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = `${(team.accent_color || ACCENT)}44`; e.currentTarget.style.background = `${(team.accent_color || ACCENT)}08`; }}>
                      {team.logo_url && (
                        <img src={team.logo_url} alt={team.name} style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} loading="lazy" />
                      )}
                      <div>
                        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: '#e8eaf6' }}>{team.name}</div>
                        {team.tagline && (
                          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.4)', letterSpacing: 1 }}>{team.tagline}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* FLEET TAB */}
        {activeTab === 'ships' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <SectionLabel>Personal Fleet</SectionLabel>
              <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 24, color: '#e8eaf6', marginBottom: 4 }}>Ships</h2>
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)' }}>
                {form.ships.reduce((sum, s) => sum + (s.quantity || 1), 0)} ships across {form.ships.length} {form.ships.length === 1 ? 'type' : 'types'}
              </p>
            </div>

            {/* Add ship — only for editors */}
            {canEdit && (
              <div style={{ marginBottom: 28, position: 'relative' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input type="text" placeholder="Search ships to add..." value={shipSearch}
                      onChange={e => { setShipSearch(e.target.value); setShowShipPicker(true); }}
                      onFocus={() => setShowShipPicker(true)}
                      style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(206,147,216,0.25)', color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }} />

                    {/* Ship picker dropdown */}
                    {showShipPicker && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: 300, overflowY: 'auto', background: '#0a0610', border: `1px solid ${ACCENT}44`, borderTop: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                        {availableShips
                          .filter(s => !shipSearch || s.name.toLowerCase().includes(shipSearch.toLowerCase()) || (s.manufacturer || '').toLowerCase().includes(shipSearch.toLowerCase()))
                          .map(ship => {
                            const owned = form.ships.find(s => s.ship_id === ship.id);
                            return (
                              <div key={ship.id} onClick={() => addShipToFleet(ship)}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', transition: 'all 0.15s', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                                onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}12`; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                <div style={{ width: 48, height: 27, flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                  {ship.image_url ? (
                                    <img src={ship.image_url} alt={ship.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 10, color: 'rgba(206,147,216,0.15)' }}>◇</div>
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: '#e8eaf6' }}>{ship.name}</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.35)', letterSpacing: 1 }}>{ship.manufacturer || ''}</div>
                                </div>
                                {owned && (
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: ACCENT }}>OWNED ×{owned.quantity}</div>
                                )}
                              </div>
                            );
                          })}
                        {availableShips.filter(s => !shipSearch || s.name.toLowerCase().includes(shipSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '20px 14px', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>NO SHIPS FOUND</div>
                        )}
                      </div>
                    )}
                  </div>
                  {showShipPicker && (
                    <button onClick={() => { setShowShipPicker(false); setShipSearch(''); }}
                      style={{ padding: '10px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(232,234,246,0.5)', fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1, cursor: 'pointer' }}>
                      Close
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Import/Export buttons live OUTSIDE the has-ships conditional so
                a user with an empty fleet can still bulk-import from hangar.link
                without first having to add a ship manually. Export is hidden
                when there's nothing to export. */}
            {canEdit && (
              // flex:1 on each button so the row fills the same horizontal
              // span as the search bar above and the filter bar below; no gap
              // left over on the right.
              <div style={{ display: 'flex', gap: 8, marginBottom: form.ships.length === 0 ? 16 : 8, flexWrap: 'wrap' }}>
                {form.ships.length > 0 && (
                  <button type="button" onClick={() => setExportOpen(true)}
                    style={{ flex: '1 1 0', minWidth: 220, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: '7px 12px', background: 'transparent', border: `1px solid ${ACCENT}55`, color: ACCENT, textTransform: 'uppercase', cursor: 'pointer' }}>
                    ↓ Export Fleet (Hangar.link JSON)
                  </button>
                )}
                <button type="button" onClick={() => setImportOpen(true)}
                  style={{ flex: '1 1 0', minWidth: 220, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: '7px 12px', background: 'transparent', border: '1px solid rgba(79,195,247,0.5)', color: '#4fc3f7', textTransform: 'uppercase', cursor: 'pointer' }}>
                  ↑ Import Fleet (Hangar.link JSON)
                </button>
                <a href="https://hangar.link/" target="_blank" rel="noreferrer"
                  style={{ flex: '1 1 0', minWidth: 180, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: '7px 12px', background: 'transparent', border: '1px solid rgba(232,234,246,0.25)', color: 'rgba(232,234,246,0.7)', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  Open Hangar.link ↗
                </a>
              </div>
            )}

            {form.ships.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: "'Space Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 2 }}>
                NO SHIPS REGISTERED
              </div>
            ) : (() => {
              const filteredEntries = applyFleetFilter(form.ships, fleetFilter, getShipInfo);
              const sorted = [...filteredEntries].sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));
              const hasPrimary = sorted.some(s => s.primary);
              return (
                <>
                  <FleetFilterBar
                    value={fleetFilter}
                    onChange={setFleetFilter}
                    accent={ACCENT}
                    totalCount={sorted.length === form.ships.length ? form.ships.length : `${sorted.length}/${form.ships.length}`}
                  />
                  {sorted.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'rgba(232,234,246,0.35)', letterSpacing: 2 }}>
                      NO SHIPS MATCH THE CURRENT FILTER
                    </div>
                  )}
                  {(() => {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sorted.map((entry, idx) => {
                    const ship = getShipInfo(entry.ship_id);
                    if (!ship) return null;
                    const isPrimary = !!entry.primary;
                    // Show divider between primary and other ships
                    const showDivider = hasPrimary && !isPrimary && idx > 0 && sorted[idx - 1]?.primary;
                    return (
                      <React.Fragment key={entry.ship_id}>
                        {showDivider && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0' }}>
                            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.2)', textTransform: 'uppercase' }}>Other Ships</span>
                            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                          </div>
                        )}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 'clamp(8px, 2vw, 14px)', padding: isPrimary ? '14px clamp(10px, 2.5vw, 16px)' : '10px clamp(10px, 2.5vw, 16px)',
                          border: `1px solid ${isPrimary ? `${ACCENT}55` : 'rgba(206,147,216,0.18)'}`,
                          background: isPrimary ? `${ACCENT}10` : 'rgba(206,147,216,0.03)',
                          transition: 'all 0.2s',
                          boxShadow: isPrimary ? `0 0 16px ${ACCENT}15, inset 0 0 20px ${ACCENT}06` : 'none',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}66`; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = isPrimary ? `${ACCENT}55` : 'rgba(206,147,216,0.18)'; }}>

                          {/* Primary star toggle */}
                          {canEdit ? (
                            <button onClick={() => toggleShipPrimary(entry.ship_id)} title={isPrimary ? 'Remove from primary' : 'Set as primary'}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0, transition: 'all 0.2s', color: isPrimary ? '#fcd657' : 'rgba(255,255,255,0.15)', textShadow: isPrimary ? '0 0 8px rgba(252,214,87,0.5)' : 'none' }}
                              onMouseEnter={e => { if (!isPrimary) e.currentTarget.style.color = 'rgba(252,214,87,0.5)'; }}
                              onMouseLeave={e => { if (!isPrimary) e.currentTarget.style.color = 'rgba(255,255,255,0.15)'; }}>
                              ★
                            </button>
                          ) : (
                            isPrimary && <span style={{ fontSize: 16, color: '#fcd657', flexShrink: 0, textShadow: '0 0 8px rgba(252,214,87,0.5)' }}>★</span>
                          )}

                          {/* Ship thumbnail */}
                          <Link to={`/fleet/${ship.id}`} style={{ width: isPrimary ? 'clamp(60px, 15vw, 80px)' : 'clamp(48px, 12vw, 64px)', height: isPrimary ? 'clamp(34px, 8.5vw, 45px)' : 'clamp(27px, 6.75vw, 36px)', flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)', border: `1px solid ${isPrimary ? `${ACCENT}33` : 'rgba(255,255,255,0.06)'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                            {ship.image_url ? (
                              <img src={ship.image_url} alt={ship.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 14, color: 'rgba(206,147,216,0.12)' }}>◇</div>
                            )}
                          </Link>

                          {/* Ship info */}
                          <Link to={`/fleet/${ship.id}`} style={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit', overflow: 'hidden' }}>
                            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: isPrimary ? 700 : 600, fontSize: isPrimary ? 17 : 15, color: '#e8eaf6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ship.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.35)', letterSpacing: 1 }}>{ship.manufacturer || ''}</span>
                              {isPrimary && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 2, color: '#fcd657', border: '1px solid rgba(252,214,87,0.35)', padding: '1px 5px', background: 'rgba(252,214,87,0.08)', whiteSpace: 'nowrap', flexShrink: 0 }}>PRIMARY</span>}
                              {(() => {
                                const status = getShipStatus(entry);
                                const isInGame = status === SHIP_STATUS.IN_GAME;
                                const color = isInGame ? '#34d399' : '#4fc3f7';
                                const label = isInGame ? 'IN-GAME' : 'PLEDGED';
                                const pillStyle = { fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 2, color, border: `1px solid ${color}55`, padding: '1px 5px', background: `${color}10`, whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'uppercase' };
                                return canEdit ? (
                                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleShipStatus(entry.ship_id); }}
                                    title={`Currently ${label.toLowerCase()} — click to switch`}
                                    style={{ ...pillStyle, cursor: 'pointer' }}>
                                    {label}
                                  </button>
                                ) : (
                                  <span style={pillStyle}>{label}</span>
                                );
                              })()}
                            </div>
                          </Link>

                          {/* Quantity controls */}
                          {canEdit ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(3px, 1vw, 6px)', flexShrink: 0 }}>
                              <button onClick={() => updateShipQuantity(entry.ship_id, (entry.quantity || 1) - 1)}
                                style={{ width: 'clamp(22px, 5.5vw, 26px)', height: 'clamp(22px, 5.5vw, 26px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(206,147,216,0.25)', color: ACCENT, cursor: 'pointer', fontFamily: "'Orbitron'", fontSize: 13, transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}20`; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}>
                                −
                              </button>
                              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 'clamp(12px, 3vw, 14px)', fontWeight: 700, color: '#e8eaf6', minWidth: 20, textAlign: 'center' }}>{entry.quantity || 1}</span>
                              <button onClick={() => updateShipQuantity(entry.ship_id, (entry.quantity || 1) + 1)}
                                style={{ width: 'clamp(22px, 5.5vw, 26px)', height: 'clamp(22px, 5.5vw, 26px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(206,147,216,0.25)', color: ACCENT, cursor: 'pointer', fontFamily: "'Orbitron'", fontSize: 13, transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}20`; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}>
                                +
                              </button>
                              <button onClick={() => removeShipFromFleet(entry.ship_id)}
                                style={{ marginLeft: 'clamp(4px, 1vw, 8px)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '2px', transition: 'color 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#ef5350'}
                                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>✕</button>
                            </div>
                          ) : (
                            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: ACCENT, flexShrink: 0 }}>
                              ×{entry.quantity || 1}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })()}
                </>
              );
            })()}

            {canEdit && (
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', minHeight: 18 }}>
                {autoSaveStatus === 'saving' && <span style={{ color: 'rgba(232,234,246,0.5)' }}>● Saving…</span>}
                {autoSaveStatus === 'saved' && <span style={{ color: '#34d399' }}>✓ Auto-saved</span>}
                {autoSaveStatus === 'error' && <span style={{ color: '#ef5350' }}>✕ Save failed — try again</span>}
                {!autoSaveStatus && <span style={{ color: 'rgba(232,234,246,0.3)' }}>Changes save automatically</span>}
              </div>
            )}
          </div>
        )}

        {/* BLUEPRINTS TAB */}
        {activeTab === 'blueprints' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <SectionLabel>Personal Catalog</SectionLabel>
              <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 24, color: '#e8eaf6', marginBottom: 4 }}>Blueprints</h2>
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)' }}>
                {(form.blueprints || []).length} {(form.blueprints || []).length === 1 ? 'blueprint' : 'blueprints'} owned
              </p>
            </div>

            {canEdit && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 240px', position: 'relative' }}>
                    <input type="text" placeholder="Search blueprints to add..." value={blueprintSearch}
                      onChange={e => { setBlueprintSearch(e.target.value); setShowBlueprintPicker(true); }}
                      onFocus={() => setShowBlueprintPicker(true)}
                      style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(252,214,87,0.25)', color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }} />
                    {showBlueprintPicker && (() => {
                      const q = blueprintSearch.trim().toLowerCase();
                      const matches = availableBlueprints.filter(b => {
                        if (blueprintCategoryFilter && b.category !== blueprintCategoryFilter) return false;
                        if (!q) return true;
                        return (b.name || '').toLowerCase().includes(q) || (b.family || '').toLowerCase().includes(q) || (b.category || '').toLowerCase().includes(q);
                      }).slice(0, 80);
                      return (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: 360, overflowY: 'auto', background: '#0a0610', border: `1px solid ${ACCENT}44`, borderTop: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                          {matches.map(bp => {
                            const owned = (form.blueprints || []).some(b => b.blueprint_id === bp.id);
                            return (
                              <div key={bp.id}
                                onClick={() => { if (!owned) addBlueprintToProfile(bp); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: owned ? 'default' : 'pointer', opacity: owned ? 0.55 : 1, borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                                onMouseEnter={e => { if (!owned) e.currentTarget.style.background = `${ACCENT}12`; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                <div style={{ width: 36, height: 36, flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                  {bp.image_url ? (
                                    <img src={bp.image_url} alt={bp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 14, color: 'rgba(252,214,87,0.18)' }}>◇</div>
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: '#e8eaf6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bp.name}</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.35)', letterSpacing: 1, textTransform: 'uppercase' }}>
                                    {bp.category}{bp.family ? ` · ${bp.family}` : ''}
                                  </div>
                                </div>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: owned ? '#34d399' : ACCENT, letterSpacing: 1, textTransform: 'uppercase' }}>
                                  {owned ? '✓ Owned' : '+ Add'}
                                </div>
                              </div>
                            );
                          })}
                          {matches.length === 0 && (
                            <div style={{ padding: '20px 14px', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>NO BLUEPRINTS FOUND</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <select value={blueprintCategoryFilter} onChange={e => setBlueprintCategoryFilter(e.target.value)}
                    style={{ padding: '10px 14px', background: '#0a0610', border: '1px solid rgba(252,214,87,0.25)', color: '#e8eaf6', fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160 }}>
                    <option value="">All Categories</option>
                    {[...new Set(availableBlueprints.map(b => b.category).filter(Boolean))].sort().map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {showBlueprintPicker && (
                    <button onClick={() => { setShowBlueprintPicker(false); setBlueprintSearch(''); }}
                      style={{ padding: '10px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(232,234,246,0.5)', fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1, cursor: 'pointer' }}>
                      Close
                    </button>
                  )}
                </div>
              </div>
            )}

            {(form.blueprints || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, border: '1px dashed rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 32, color: 'rgba(255,255,255,0.08)', marginBottom: 12 }}>◇</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.3)' }}>
                  No blueprints registered yet.
                </div>
              </div>
            ) : (() => {
              // Bucket owned blueprints into section → category → entries[].
              // Unknown IDs (deleted from catalog) collect into their own bucket
              // so they keep rendering with the red remove chip.
              const sections = {};
              const unknownEntries = [];
              (form.blueprints || []).forEach(entry => {
                const bp = getBlueprintInfo(entry.blueprint_id);
                if (!bp) { unknownEntries.push(entry); return; }
                const secLabel = blueprintSectionOf(bp);
                const catLabel = bp.category || 'Uncategorized';
                if (!sections[secLabel]) sections[secLabel] = {};
                if (!sections[secLabel][catLabel]) sections[secLabel][catLabel] = [];
                sections[secLabel][catLabel].push({ entry, bp });
              });
              // Section render order: defined order, then "Other", then "Unknown".
              const orderedLabels = [...BLUEPRINT_SECTIONS.map(s => s.label), 'Other'].filter(l => sections[l]);

              const renderCard = ({ entry, bp }) => (
                <div key={entry.blueprint_id} style={{ position: 'relative', display: 'flex', gap: 10, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', padding: 12 }}>
                  <div style={{ width: 56, height: 56, flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    {bp.image_url ? (
                      <img src={bp.image_url} alt={bp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 22, color: 'rgba(252,214,87,0.18)' }}>◇</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: canEdit ? 28 : 0 }}>
                    <Link to={`/resources/blueprints/${bp.id}`} style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: '#e8eaf6', textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {bp.name}
                    </Link>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.35)', letterSpacing: 1, textTransform: 'uppercase' }}>
                      {bp.category}{bp.family ? ` · ${bp.family}` : ''}
                    </div>
                  </div>
                  {canEdit && (
                    <button onClick={() => removeBlueprintFromProfile(entry.blueprint_id)} title="Remove blueprint"
                      style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, background: 'transparent', border: '1px solid rgba(239,83,80,0.3)', color: '#ef5350', fontFamily: "'Orbitron'", fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  )}
                </div>
              );

              return (
                <div>
                  {orderedLabels.map(secLabel => {
                    const catMap = sections[secLabel];
                    const sectionTotal = Object.values(catMap).reduce((acc, list) => acc + list.length, 0);
                    return (
                      <div key={secLabel} style={{ marginBottom: 28 }}>
                        {/* Section header — accent bar + label + count, like the in-game inventory UI */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(252,214,87,0.08)', borderLeft: '3px solid #fcd657', borderTop: '1px solid rgba(252,214,87,0.18)', borderRight: '1px solid rgba(252,214,87,0.18)', borderBottom: '1px solid rgba(252,214,87,0.18)', marginBottom: 14 }}>
                          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 3, color: '#fcd657', textTransform: 'uppercase' }}>
                            {secLabel}
                          </span>
                          <div style={{ flex: 1, height: 1, background: 'rgba(252,214,87,0.18)' }} />
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.5)', letterSpacing: 1 }}>
                            {sectionTotal}
                          </span>
                        </div>

                        {/* Subcategory blocks within the section, alphabetical */}
                        {Object.entries(catMap)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([catLabel, list]) => (
                            <div key={catLabel} style={{ marginBottom: 18 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 4 }}>
                                <span style={{ color: '#fcd657', fontSize: 10, lineHeight: 1 }}>◆</span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: 'rgba(232,234,246,0.7)', textTransform: 'uppercase' }}>
                                  {catLabel}
                                </span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.3)', letterSpacing: 1 }}>
                                  {list.length}
                                </span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                                {list
                                  .sort((a, b) => (a.bp.name || '').localeCompare(b.bp.name || ''))
                                  .map(renderCard)}
                              </div>
                            </div>
                          ))}
                      </div>
                    );
                  })}

                  {/* Unknown-blueprint chips — keep them visible so the user can remove dangling refs */}
                  {unknownEntries.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(239,83,80,0.08)', borderLeft: '3px solid #ef5350', borderTop: '1px solid rgba(239,83,80,0.2)', borderRight: '1px solid rgba(239,83,80,0.2)', borderBottom: '1px solid rgba(239,83,80,0.2)', marginBottom: 14 }}>
                        <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 3, color: '#ef5350', textTransform: 'uppercase' }}>
                          Unknown
                        </span>
                        <div style={{ flex: 1, height: 1, background: 'rgba(239,83,80,0.2)' }} />
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.5)', letterSpacing: 1 }}>
                          {unknownEntries.length}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                        {unknownEntries.map(entry => (
                          <div key={entry.blueprint_id} style={{ padding: 12, border: '1px solid rgba(239,83,80,0.3)', background: 'rgba(239,83,80,0.05)', fontFamily: "'Space Mono', monospace", fontSize: 9, color: '#ef5350' }}>
                            Unknown blueprint ({entry.blueprint_id})
                            {canEdit && (
                              <button onClick={() => removeBlueprintFromProfile(entry.blueprint_id)} style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#ef5350', cursor: 'pointer' }}>×</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {canEdit && (
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', minHeight: 18 }}>
                {autoSaveStatus === 'saving' && <span style={{ color: 'rgba(232,234,246,0.5)' }}>● Saving…</span>}
                {autoSaveStatus === 'saved' && <span style={{ color: '#34d399' }}>✓ Auto-saved</span>}
                {autoSaveStatus === 'error' && <span style={{ color: '#ef5350' }}>✕ Save failed — try again</span>}
                {!autoSaveStatus && <span style={{ color: 'rgba(232,234,246,0.3)' }}>Changes save automatically</span>}
              </div>
            )}
          </div>
        )}

        {/* EVENTS TAB */}
        {activeTab === 'events' && (() => {
          const now = new Date();
          const isAttendingEvent = (ev) => {
            const attendees = normalizeEventAttendees(ev.attendees);
            return myProfile ? attendees.some(a => a.user_id === myProfile.id) : false;
          };
          const upcoming = userEvents.filter(ev => {
            const d = ev.date ? new Date(`${ev.date}T${ev.time || '23:59'}`) : null;
            return !d || d >= now;
          }).sort((a, b) => {
            const da = a.date ? new Date(`${a.date}T${a.time || '00:00'}`) : new Date('9999');
            const db = b.date ? new Date(`${b.date}T${b.time || '00:00'}`) : new Date('9999');
            return da - db;
          });
          const past = userEvents.filter(ev => {
            const d = ev.date ? new Date(`${ev.date}T${ev.time || '23:59'}`) : null;
            return d && d < now;
          }).sort((a, b) => {
            const da = new Date(`${a.date}T${a.time || '00:00'}`);
            const db = new Date(`${b.date}T${b.time || '00:00'}`);
            return db - da;
          });

          return (
            <div>
              <div style={{ marginBottom: 28 }}>
                <SectionLabel>Scheduled</SectionLabel>
                <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 24, color: '#e8eaf6', marginBottom: 4 }}>Events</h2>
                <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)' }}>
                  {userEvents.length} event{userEvents.length !== 1 ? 's' : ''} {isOwner ? "you're attending" : 'this member is attending'}
                </p>
              </div>

              {userEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: "'Space Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 2 }}>
                  NO EVENTS
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                  {upcoming.length > 0 && (
                    <div>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: '#34d399', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 24, height: 1, background: '#34d399' }} /> Upcoming ({upcoming.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {upcoming.map(ev => (
                          <EventCard
                            key={ev.id}
                            event={ev}
                            canEdit={false}
                            canJoin={!!myProfile}
                            onEdit={() => {}}
                            onArchive={() => {}}
                            onRSVP={profileToggleRSVP}
                            isAttending={isAttendingEvent(ev)}
                            profile={myProfile}
                            onManageAttendees={() => {}}
                            onShareToOrg={() => {}}
                            canShareToOrg={false}
                            reviewData={null}
                            hasReminder={!!userReminders[ev.id]}
                            reminderSchedule={userReminders[ev.id] || null}
                            onToggleReminder={profileToggleReminder}
                            isHighlighted={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {past.length > 0 && (
                    <div>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: 'rgba(232,234,246,0.3)', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 24, height: 1, background: 'rgba(232,234,246,0.15)' }} /> Past ({past.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {past.map(ev => (
                          <EventCard
                            key={ev.id}
                            event={ev}
                            canEdit={false}
                            canJoin={!!myProfile}
                            onEdit={() => {}}
                            onArchive={() => {}}
                            onRSVP={profileToggleRSVP}
                            isAttending={isAttendingEvent(ev)}
                            profile={myProfile}
                            onManageAttendees={() => {}}
                            onShareToOrg={() => {}}
                            canShareToOrg={false}
                            reviewData={null}
                            hasReminder={!!userReminders[ev.id]}
                            reminderSchedule={userReminders[ev.id] || null}
                            onToggleReminder={profileToggleReminder}
                            isHighlighted={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* MY CONTRACTS TAB — owner only */}
        {activeTab === 'contracts' && isOwner && (() => {
          const contractEvents = userEvents.filter(ev => ev.is_contract);
          const now = new Date();
          const ongoing = contractEvents.filter(ev => !isEventCompleted(ev));
          const concluded = contractEvents.filter(ev => isEventCompleted(ev));

          // Rollup totals for the header
          let owedTotal = 0;
          let paidTotal = 0;
          let confirmedTotal = 0;
          const byCurrency = {};
          concluded.forEach(ev => {
            const currency = getCompensationCurrency(ev) || 'aUEC';
            const savedPayouts = Array.isArray(ev.payouts) ? ev.payouts : [];
            const mineSaved = savedPayouts.find(p => p.user_id === myProfile.id);
            const calc = calculateEventPayouts(ev);
            const mineCalc = calc.rows.find(r => r.user_id === myProfile.id);
            const owed = mineSaved?.total_owed ?? mineCalc?.total_owed ?? 0;
            if (!byCurrency[currency]) byCurrency[currency] = { owed: 0, paid: 0, confirmed: 0 };
            byCurrency[currency].owed += owed;
            if (mineSaved?.paid) byCurrency[currency].paid += owed;
            if (mineSaved?.confirmed) byCurrency[currency].confirmed += owed;
            owedTotal += owed;
            if (mineSaved?.paid) paidTotal += owed;
            if (mineSaved?.confirmed) confirmedTotal += owed;
          });

          function fmt(n) { return (Number(n) || 0).toLocaleString(); }

          function renderContractCard(ev) {
            const currency = getCompensationCurrency(ev) || 'aUEC';
            const savedPayouts = Array.isArray(ev.payouts) ? ev.payouts : [];
            const mineSaved = savedPayouts.find(p => p.user_id === myProfile.id);
            const calc = calculateEventPayouts(ev);
            const mineCalc = calc.rows.find(r => r.user_id === myProfile.id);
            const owed = mineSaved?.total_owed ?? mineCalc?.total_owed ?? 0;
            const breakdown = mineCalc?.breakdown || {};
            const leaderMarkedPaid = !!mineSaved?.paid;
            const iConfirmed = !!mineSaved?.confirmed;
            const completed = isEventCompleted(ev);

            const breakdownParts = [];
            if (breakdown.flat) breakdownParts.push(`Flat ${fmt(breakdown.flat)}`);
            if (breakdown.group) breakdownParts.push(`${breakdown.group.name} ${fmt(breakdown.group.rate)}`);
            if (breakdown.position) breakdownParts.push(`${breakdown.position.name} ${fmt(breakdown.position.rate)}`);
            if (breakdown.rsvp) breakdownParts.push(`${breakdown.rsvp.name} ${fmt(breakdown.rsvp.rate)}`);
            (breakdown.bonuses || []).forEach(b => breakdownParts.push(`${b.name} ${fmt(b.rate)}`));

            return (
              <div key={ev.id} style={{
                padding: '14px 16px',
                border: `1px solid ${leaderMarkedPaid ? 'rgba(52,211,153,0.35)' : 'rgba(255,255,255,0.08)'}`,
                background: leaderMarkedPaid ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.02)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/events/${ev.id}/review`} style={{
                      fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 15, color: '#e8eaf6',
                      textDecoration: 'none',
                    }}>{ev.title}</Link>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.45)', letterSpacing: 1, marginTop: 4 }}>
                      {ev.date && new Date(ev.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      {ev.team && ` · ${ev.team}`}
                      {ev.is_org_wide && ' · ORG-WIDE'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: leaderMarkedPaid ? '#34d399' : ACCENT }}>
                      {fmt(owed)} <span style={{ fontSize: 11, color: 'rgba(232,234,246,0.5)' }}>{currency}</span>
                    </div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.4)', textTransform: 'uppercase', marginTop: 2 }}>
                      {completed ? 'Owed' : 'Estimated'}
                    </div>
                  </div>
                </div>

                {breakdownParts.length > 0 && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.4)', letterSpacing: 1, marginBottom: 10 }}>
                    {breakdownParts.join(' + ')}
                  </div>
                )}

                {/* Status row + confirm control */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {/* Leader-paid indicator */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 14, height: 14, borderRadius: 2,
                      background: leaderMarkedPaid ? '#34d399' : 'transparent',
                      border: `1.5px solid ${leaderMarkedPaid ? '#34d399' : 'rgba(255,255,255,0.2)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {leaderMarkedPaid && <span style={{ color: '#0a0610', fontSize: 10, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: leaderMarkedPaid ? '#34d399' : 'rgba(232,234,246,0.4)', textTransform: 'uppercase' }}>
                      {leaderMarkedPaid ? 'Leader marked paid' : 'Awaiting payment'}
                    </span>
                    {mineSaved?.paid_at && (
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.3)', letterSpacing: 1 }}>
                        · {new Date(mineSaved.paid_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Confirm received — only meaningful once the event has concluded */}
                  {completed && (
                    <button
                      onClick={() => confirmContractPayment(ev, !iConfirmed)}
                      disabled={!leaderMarkedPaid && !iConfirmed}
                      title={!leaderMarkedPaid && !iConfirmed ? 'The leader must mark this as paid first' : ''}
                      style={{
                        fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase',
                        cursor: (!leaderMarkedPaid && !iConfirmed) ? 'not-allowed' : 'pointer',
                        padding: '6px 12px',
                        background: iConfirmed ? 'rgba(52,211,153,0.18)' : 'transparent',
                        border: `1px solid ${iConfirmed ? '#34d399' : 'rgba(255,255,255,0.2)'}`,
                        color: iConfirmed ? '#34d399' : 'rgba(232,234,246,0.6)',
                        opacity: (!leaderMarkedPaid && !iConfirmed) ? 0.35 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      {iConfirmed ? '✓ I received this' : 'Confirm receipt'}
                    </button>
                  )}
                  {!completed && (
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                      Confirmation available after event concludes
                    </span>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div>
              <div style={{ marginBottom: 28 }}>
                <SectionLabel>Paid Work</SectionLabel>
                <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 24, color: '#e8eaf6', marginBottom: 4 }}>My Contracts</h2>
                <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)' }}>
                  Compensation from contract events you've signed up for. Confirm receipt once you've been paid.
                </p>
              </div>

              {contractEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: "'Space Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 2 }}>
                  NO CONTRACT EVENTS YET
                </div>
              ) : (
                <>
                  {/* Rollup cards */}
                  {concluded.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 28 }}>
                      {Object.entries(byCurrency).map(([currency, totals]) => (
                        <div key={currency} style={{ padding: '12px 16px', border: '1px solid rgba(52,211,153,0.25)', background: 'rgba(52,211,153,0.04)' }}>
                          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: 'rgba(232,234,246,0.4)', textTransform: 'uppercase' }}>
                            {currency}
                          </div>
                          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: '#34d399', marginTop: 4 }}>
                            {fmt(totals.owed)}
                          </div>
                          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.5)', letterSpacing: 1, marginTop: 4 }}>
                            {fmt(totals.paid)} paid · {fmt(totals.confirmed)} confirmed
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {concluded.length > 0 && (
                      <div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: '#34d399', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 24, height: 1, background: '#34d399' }} /> Concluded ({concluded.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {concluded.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map(renderContractCard)}
                        </div>
                      </div>
                    )}
                    {ongoing.length > 0 && (
                      <div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 24, height: 1, background: ACCENT }} /> Upcoming / In Progress ({ongoing.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {ongoing.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0)).map(renderContractCard)}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* AVAILABILITY TAB */}
        {activeTab === 'availability' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <SectionLabel>Gaming Schedule</SectionLabel>
              <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 24, color: '#e8eaf6', marginBottom: 8 }}>Availability</h2>
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)' }}>
                {canEdit ? "Set your weekly gaming windows so your crew knows when you're online." : "This member's weekly gaming availability."}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {DAYS.map((day, i) => {
                const windows = getWindows(day);
                const isActive = windows.length > 0;
                return (
                  <div key={day} style={{ padding: '14px 20px', border: `1px solid ${isActive ? `${ACCENT}44` : 'rgba(255,255,255,0.06)'}`, background: isActive ? `${ACCENT}06` : 'rgba(255,255,255,0.01)', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>

                      {/* Toggle — only for editors */}
                      {canEdit ? (
                        <div onClick={() => toggleAvailDay(day)} style={{ width: 40, height: 22, borderRadius: 11, background: isActive ? ACCENT : 'rgba(255,255,255,0.1)', border: `1px solid ${isActive ? ACCENT : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', position: 'relative', transition: 'all 0.3s', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', top: 3, left: isActive ? 20 : 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.3s' }} />
                        </div>
                      ) : (
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: isActive ? ACCENT : 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
                      )}

                      {/* Day label */}
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: isActive ? '#e8eaf6' : 'rgba(232,234,246,0.3)', textTransform: 'uppercase', width: 36, flexShrink: 0 }}>{DAY_LABELS[i]}</div>

                      {!isActive && (
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>OFFLINE</span>
                      )}

                      {/* Read-only: show all windows inline */}
                      {isActive && !canEdit && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {windows.map((win, wi) => (
                            <span key={wi} style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: ACCENT, letterSpacing: 1 }}>
                              {fmtHour(win.start)} – {fmtHour(win.end)}{wi < windows.length - 1 ? ',' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Editable: time windows */}
                    {isActive && canEdit && (
                      <div style={{ marginTop: 10, marginLeft: 56, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {windows.map((win, wi) => (
                          <div key={wi} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.4)' }}>FROM</span>
                              <select value={win.start} onChange={e => updateAvailTime(day, wi, 'start', e.target.value)}
                                style={{ padding: '4px 8px', background: '#0d1120', border: `1px solid ${ACCENT}44`, color: '#e8eaf6', fontFamily: "'Space Mono', monospace", fontSize: 10, borderRadius: 2 }}>
                                {Array.from({ length: 24 }, (_, h) => (
                                  <option key={h} value={h}>{fmtHour(h)}</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: 'rgba(232,234,246,0.4)' }}>TO</span>
                              <select value={win.end} onChange={e => updateAvailTime(day, wi, 'end', e.target.value)}
                                style={{ padding: '4px 8px', background: '#0d1120', border: `1px solid ${ACCENT}44`, color: '#e8eaf6', fontFamily: "'Space Mono', monospace", fontSize: 10, borderRadius: 2 }}>
                                {Array.from({ length: 24 }, (_, h) => (
                                  <option key={h} value={h + 1}>{fmtHour(h + 1)}</option>
                                ))}
                              </select>
                            </div>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: ACCENT }}>
                              {fmtHour(win.start)} – {fmtHour(win.end)}
                            </span>
                            {windows.length > 1 && (
                              <button onClick={() => removeWindow(day, wi)}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '2px', transition: 'color 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#ef5350'}
                                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>✕</button>
                            )}
                          </div>
                        ))}
                        <button onClick={() => addWindow(day)}
                          style={{ alignSelf: 'flex-start', fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: ACCENT, background: 'transparent', border: `1px dashed ${ACCENT}44`, padding: '4px 12px', cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.background = `${ACCENT}10`; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = `${ACCENT}44`; e.currentTarget.style.background = 'transparent'; }}>
                          + Add Window
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {canEdit && (
              <button onClick={handleSave} disabled={saving} style={{ marginTop: 28, padding: '12px 32px', background: 'transparent', border: `2px solid ${ACCENT}`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s', clipPath: 'polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)' }}
                onMouseEnter={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.color = '#05070f'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = ACCENT; }}>
                {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Availability'}
              </button>
            )}
          </div>
        )}

        {/* LISTINGS TAB */}
        {activeTab === 'listings' && (
          <div>
            <SectionLabel>Market Listings</SectionLabel>
            <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: '#e8eaf6', marginBottom: 20 }}>
              {isOwner ? 'My Listings' : `${profile.discord_username}'s Listings`}
            </h2>
            {myListings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: 'rgba(255,255,255,0.06)', marginBottom: 12 }}>◇</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.25)' }}>No active listings.</div>
                {isOwner && <Link to="/market" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, color: ACCENT, display: 'inline-block', marginTop: 12 }}>GO TO MARKET →</Link>}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {myListings.map(l => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', border: `1px solid ${l.status === 'sold_out' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.08)'}`, background: 'rgba(255,255,255,0.02)', opacity: l.status === 'sold_out' ? 0.6 : 1 }}>
                    <div style={{ width: 48, height: 48, flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
                      {l.image_url ? <img src={l.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 14, color: `${ACCENT}20` }}>◇</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: '#e8eaf6' }}>{l.title}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: '1px 6px', border: `1px solid ${ACCENT}33`, color: ACCENT }}>{l.store_type.toUpperCase()}</span>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: l.status === 'active' ? '#34d399' : '#ef5350' }}>{l.status.toUpperCase()}</span>
                        {l.accepts_merits && l.merit_price && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: '#fcd657' }}>{l.merit_price} Merits</span>}
                        {l.accepts_auec && l.auec_price && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: '#4fc3f7' }}>{Number(l.auec_price).toLocaleString()} aUEC</span>}
                        {!l.is_permanent && l.quantity != null && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.3)' }}>{l.quantity_sold}/{l.quantity} sold</span>}
                      </div>
                    </div>
                    {isOwner && l.status === 'active' && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => markListingSold(l.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: 'none', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', cursor: 'pointer' }}>Mark Sold</button>
                        <button onClick={() => removeMyListing(l.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: 'none', border: '1px solid rgba(239,83,80,0.3)', color: '#ef5350', cursor: 'pointer' }}>Remove</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TRANSACTIONS TAB (purchases) */}
        {activeTab === 'transactions' && (
          <div>
            <SectionLabel>Purchase History</SectionLabel>
            <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: '#e8eaf6', marginBottom: 20 }}>Transactions</h2>
            {myTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: 'rgba(255,255,255,0.06)', marginBottom: 12 }}>◇</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.25)' }}>No transactions yet.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {myTransactions.map(t => (
                  <div key={t.id} style={{ padding: '14px 18px', border: `1px solid ${t.status === 'pending' ? 'rgba(252,214,87,0.25)' : t.status === 'confirmed' ? 'rgba(52,211,153,0.15)' : 'rgba(239,83,80,0.15)'}`, background: t.status === 'pending' ? 'rgba(252,214,87,0.04)' : 'rgba(255,255,255,0.02)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 40, height: 40, flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
                          {t.item_image_url ? <img src={t.item_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 12, color: `${ACCENT}20` }}>◇</div>}
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: '#e8eaf6' }}>{t.item_title}</div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: '1px 6px', border: `1px solid ${ACCENT}33`, color: ACCENT }}>{t.store_type.toUpperCase()}</span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: t.payment_type === 'merits' ? '#fcd657' : t.payment_type === 'auec' ? '#4fc3f7' : '#34d399' }}>
                              {t.payment_type === 'merits' && `${t.merit_amount} Merits`}
                              {t.payment_type === 'auec' && `${Number(t.auec_amount).toLocaleString()} aUEC`}
                              {t.payment_type === 'cash' && `$${Number(t.cash_amount).toFixed(2)}`}
                            </span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.25)' }}>{new Date(t.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: t.status === 'pending' ? '#fcd657' : t.status === 'confirmed' ? '#34d399' : '#ef5350', padding: '4px 12px', border: `1px solid ${t.status === 'pending' ? 'rgba(252,214,87,0.3)' : t.status === 'confirmed' ? 'rgba(52,211,153,0.3)' : 'rgba(239,83,80,0.3)'}` }}>{t.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SELL HISTORY TAB */}
        {activeTab === 'sell_history' && (
          <div>
            <SectionLabel>Completed Sales</SectionLabel>
            <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: '#e8eaf6', marginBottom: 20 }}>Sell History</h2>
            {mySellHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: 'rgba(255,255,255,0.06)', marginBottom: 12 }}>◇</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.25)' }}>No completed sales.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mySellHistory.map(t => (
                  <div key={t.id} style={{ padding: '14px 18px', border: '1px solid rgba(52,211,153,0.15)', background: 'rgba(52,211,153,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: '#e8eaf6' }}>{t.item_title}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: 'rgba(232,234,246,0.45)' }}>Buyer: {t.buyer?.discord_username || 'Unknown'}</span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: t.payment_type === 'merits' ? '#fcd657' : t.payment_type === 'auec' ? '#4fc3f7' : '#34d399' }}>
                            {t.payment_type === 'merits' && `${t.merit_amount} Merits`}
                            {t.payment_type === 'auec' && `${Number(t.auec_amount).toLocaleString()} aUEC`}
                            {t.payment_type === 'cash' && `$${Number(t.cash_amount).toFixed(2)}`}
                          </span>
                          {t.quantity > 1 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.3)' }}>×{t.quantity}</span>}
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: 'rgba(232,234,246,0.25)' }}>{t.completed_at ? new Date(t.completed_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                      <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, color: '#34d399' }}>SOLD</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS TAB — Super Admin Only */}
        {activeTab === 'analytics' && viewerIsSuperAdmin && (() => {
          return <ProfileAnalytics profileId={profile.id} profileName={profile.discord_username} />;
        })()}
      </div>

      {/* FOOTER */}
      <footer style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.07)', padding: '32px 40px', background: 'rgba(0,0,0,0.4)', marginTop: 60 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: 'rgba(232,234,246,0.18)', lineHeight: 1.8 }}>
          © 2026 Starkeeper Industries. All rights reserved.
        </div>
      </footer>

      {/* DIRECTORY MODAL — opened by clicking Home Base / Timezone overview cards */}
      {directoryModal && (
        <div
          onClick={() => setDirectoryModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#0a0610',
              border: `1px solid ${ACCENT}55`,
              boxShadow: `0 0 40px ${ACCENT}25, inset 0 0 40px rgba(0,0,0,0.6)`,
              maxWidth: 560, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              clipPath: 'polygon(14px 0%, 100% 0%, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0% 100%, 0% 14px)',
            }}
          >
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${ACCENT}22`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: 'uppercase', marginBottom: 4 }}>
                  {directoryModal.type === 'home_base' ? 'Home Base' : 'Timezone'}
                </div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: '#e8eaf6' }}>
                  {directoryModal.type === 'timezone'
                    ? directoryModal.value.replace(/_/g, ' ').replace(/^.*\//, '')
                    : directoryModal.value}
                </div>
                {directoryModal.type === 'timezone' && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: 'rgba(232,234,246,0.45)', marginTop: 4 }}>
                    Includes neighbouring time zones (+/-6 hours)
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {directoryModal.type === 'home_base' && myProfile && directoryModal.value && myProfile.home_base !== directoryModal.value && (
                  <button
                    onClick={async () => {
                      // Optimistically update local state, then persist through
                      // AuthContext.updateProfile so the shared myProfile stays in sync.
                      if (updateAuthProfile) {
                        const { data } = await updateAuthProfile({ home_base: directoryModal.value });
                        if (data) setViewedProfile(data);
                      } else {
                        await supabase.from('profiles').update({ home_base: directoryModal.value }).eq('id', myProfile.id);
                        const { data } = await supabase.from('profiles').select('*').eq('id', myProfile.id).single();
                        setViewedProfile(data);
                      }
                      setDirectoryModal(null);
                    }}
                    style={{ background: `${ACCENT}18`, border: `1px solid ${ACCENT}`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase' }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}30`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${ACCENT}18`; }}
                  >
                    Set as my Home Base
                  </button>
                )}
                {directoryModal.type === 'home_base' && myProfile && myProfile.home_base === directoryModal.value && (
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: '#34d399', textTransform: 'uppercase', padding: '6px 10px', border: '1px solid rgba(52,211,153,0.4)' }}>
                    ✓ Your Home Base
                  </span>
                )}
                <button
                  onClick={() => setDirectoryModal(null)}
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(232,234,246,0.6)', fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = ACCENT; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(232,234,246,0.6)'; }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 0' }}>
              {directoryModal.loading ? (
                <div style={{ padding: 40, textAlign: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: `${ACCENT}88` }}>LOADING…</div>
              ) : directoryModal.members.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, color: 'rgba(232,234,246,0.3)' }}>NO MATCHES FOUND</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '6px 22px 12px', fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: 'rgba(232,234,246,0.4)', textTransform: 'uppercase' }}>
                    {directoryModal.members.length} {directoryModal.members.length === 1 ? 'member' : 'members'}
                  </div>
                  {directoryModal.members.map(m => (
                    <Link
                      key={m.id}
                      to={`/profile/${m.id}`}
                      onClick={() => setDirectoryModal(null)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 22px', textDecoration: 'none', color: 'inherit', transition: 'background 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}10`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: 38, height: 38, flexShrink: 0, borderRadius: '50%', overflow: 'hidden', border: `1px solid ${ACCENT}33`, background: '#0d1120' }}>
                        {m.discord_avatar ? (
                          <img src={m.discord_avatar} alt={m.discord_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron'", fontSize: 14, color: ACCENT }}>{(m.discord_username || '?').charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: '#e8eaf6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.discord_username}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                          {m.org_role && (
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.2, color: 'rgba(232,234,246,0.4)', textTransform: 'uppercase' }}>{m.org_role}</span>
                          )}
                          {directoryModal.type === 'timezone' && m.timezone && m.timezone !== directoryModal.value && (
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: 'rgba(232,234,246,0.55)', textTransform: 'uppercase' }}>
                              {m.timezone.replace(/_/g, ' ').replace(/^.*\//, '')}
                            </span>
                          )}
                        </div>
                      </div>
                      {directoryModal.type === 'timezone' && (
                        (() => {
                          // Signed delta from the viewer's selected tz so the
                          // chip reads "-1h" for someone behind us, "+1h" for
                          // someone ahead. Chicago viewer + New Yorker → +1h.
                          // New York viewer + Chicagoan → -1h. (UTC-5 minus
                          // UTC-6 = +1; UTC-6 minus UTC-5 = -1.)
                          const tgt = directoryModal.targetOffset || 0;
                          const off = typeof m.offsetHours === 'number' ? m.offsetHours : tgt;
                          const signed = Math.round((off - tgt) * 10) / 10;
                          const abs = Math.abs(signed);
                          const exact = abs < 0.5;
                          const sign = signed >= 0 ? '+' : '-';
                          const label = exact ? 'Exact' : (abs < 1 ? `${sign}<1h` : `${sign}${abs}h`);
                          const bg = exact ? 'rgba(52,211,153,0.16)' : abs <= 1 ? 'rgba(252,214,87,0.14)' : 'rgba(206,147,216,0.14)';
                          const fg = exact ? '#34d399' : abs <= 1 ? '#fcd657' : '#ce93d8';
                          return (
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, padding: '3px 8px', background: bg, color: fg, border: `1px solid ${fg}55`, textTransform: 'uppercase', flexShrink: 0 }}>{label}</span>
                          );
                        })()
                      )}
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: ACCENT, letterSpacing: 1 }}>→</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hangar.link compatibility — only mount when the modal is open so the
          ship list / file picker don't render until needed. */}
      <FleetExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        currentShips={form.ships}
        scShips={availableShips}
        handle={profile?.discord_username || profile?.discord_handle}
      />
      <FleetImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        currentShips={form.ships}
        scShips={availableShips}
        onApply={(nextShips) => setForm(f => ({ ...f, ships: nextShips }))}
      />
    </div>
  );
}
